import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IInitialState } from '../../../../reduxService';
import { ISectorResponse, ISubSectorResponse } from '../../api/IApiResponse';
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
import { IGeometryForm } from '../../components/InputForm/Geometry/Geometry';
import { IHydraulicDataForm } from '../../components/InputForm/HydraulicData/IHydraulicData';
import { IMaterialResponse } from '../../../api/IApiResponse';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { IFetchTemplatesPayload } from '../../../api/common';

export interface IProjectMacra1 {
    hydraulicData?: IHydraulicDataForm;
    geometry?: ISectorResponse[];
}

export interface IProjectMacra1WithInfo extends IProjectMacra1 {
    projectInfo: IProjectInfoForm;
}

const initialState: IInitialState<IProjectMacra1> = {
    initialState: true,
};

export interface IMaterialSectorSlice {
    sector: number;
    materialId: number;
    material: IMaterialResponse;
}
export interface IMaterialSubSectorSlice {
    sectorId: number;
    subsectors: ISubSectorResponse[];
}

export const projectSlice = createSlice({
    name: 'projectMacra1',
    initialState,
    reducers: {
        cleanProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        cleanGeometry: state => {
            state.initialState = false;
            state.data = {
                ...state.data,
                geometry: undefined,
            };
        },
        setProject: (state, action: PayloadAction<IProjectMacra1>) => {
            state.initialState = false;
            state.data = action.payload;
        },
        setHydraulicData: (state, action: PayloadAction<IHydraulicDataForm>) => {
            state.initialState = false;
            state.data = { ...state.data, hydraulicData: action.payload };
        },
        setSector: (state, action: PayloadAction<IGeometryForm>) => {
            state.initialState = false;
            if (!action.payload.isActive) {
                switch (action.payload.sector) {
                    case 6:
                        state.data!.geometry = state.data!.geometry!.filter(geo => {
                            if (geo.channel === 7) return false;
                            if (geo.channel !== action.payload.sector) return true;
                            return false;
                        });
                        break;
                    case 2:
                        state.data!.geometry = state.data!.geometry!.filter(geo => {
                            if (geo.channel === 1) return false;
                            if (geo.channel !== action.payload.sector) return true;
                            return false;
                        });
                        break;
                    default:
                        state.data!.geometry = state.data!.geometry!.filter(geo => geo.channel !== action.payload.sector || geo.channel === 3 || geo.channel === 4 || geo.channel === 5);

                        break;
                }
            } else {
                let index = state.data!.geometry!.findIndex(sec => sec.channel === action.payload.sector);
                if (index === -1) {
                    let newSector: ISectorResponse = {
                        inclination: action.payload.inclination,
                        width: action.payload.width,
                        height: action.payload.height,
                        angle: action.payload.angle,
                        length: action.payload.length,
                        channel: action.payload.sector,
                        channelType: action.payload.channelType,
                        isActive: action.payload.isActive,
                        material: action.payload.material,
                    };
                    state.data!.geometry!.push(newSector);
                } else {
                    state.data!.geometry = state.data!.geometry!.map((content, i) =>
                        i === index
                            ? {
                                  ...content,
                                  inclination: action.payload.inclination,
                                  width: action.payload.width,
                                  height: action.payload.height,
                                  angle: action.payload.angle,
                                  length: action.payload.length,
                                  isActive: action.payload.isActive,
                                  subSectors: [],
                              }
                            : content,
                    );
                }
            }
        },
        setMaterialToSectors: (state, action: PayloadAction<IMaterialSectorSlice>) => {
            state.data?.geometry!.forEach((sec, index) => {
                state.data!.geometry![index].materialId = action.payload.materialId;
                state.data!.geometry![index].material = action.payload.material;
            });
            state.initialState = false;
        },
        setMaterialToSector: (state, action: PayloadAction<IMaterialSectorSlice>) => {
            let index = state.data?.geometry!.findIndex(sec => sec.channel === action.payload.sector);
            state.initialState = false;
            state.data!.geometry![index!].materialId = action.payload.materialId;
            state.data!.geometry![index!].material = action.payload.material;
        },
        setMaterialToSubSectors: (state, action: PayloadAction<IMaterialSectorSlice>) => {
            state.data?.geometry!.forEach((sec, index) => {
                state.data!.geometry![index].subSectors = sec.subSectors?.map(subs => ({ ...subs, material: action.payload.material }));
            });
            state.initialState = false;
        },
        setMaterialToSubSector: (state, action: PayloadAction<IMaterialSubSectorSlice>) => {
            let index = state.data?.geometry!.findIndex(sec => sec.channel === action.payload.sectorId);
            state.initialState = false;
            state.data!.geometry![index!].subSectors = action.payload.subsectors;
        },
        setSectors: (state, action: PayloadAction<ISectorResponse[]>) => {
            state.initialState = false;
            state.data = { ...state.data, geometry: action.payload };
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchTemplateById.pending, state => {
                state.data = { ...state.data };
                state.pending = true;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchTemplateById.fulfilled, (state, action) => {
                state.data = { ...state.data, geometry: action.payload.sectors, hydraulicData: action.payload.hydraulicData};
                state.pending = false;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchTemplateById.rejected, (state, action) => {
                state.data = undefined;
                state.pending = false;
                state.error = {
                    status: (action.payload as any)?.status ?? 0,
                    data: (action.payload as any)?.data ?? 'invalid template response',
                };
                state.initialState = false;
            });
    },
});

export const fetchTemplateById = createAsyncThunk('api/Template/GetTemplateCBPById', async (payload: IFetchTemplatesPayload, { rejectWithValue }) => {
    try {
        const res = await BeMaccaferriStoreHelper.getTemplateCBPByIdAsync(payload.id);
        return res.data;
    } catch (error: any) {
        return rejectWithValue(error.response);
    }
});

export const { setHydraulicData, setSector, setMaterialToSubSectors, setMaterialToSectors, setMaterialToSector, setMaterialToSubSector, setProject, cleanProject, cleanGeometry, setSectors } =
    projectSlice.actions;

export default projectSlice.reducer;
